import React, { memo } from "react";
import { Link } from "react-router-dom";
import { EmptyData, fmtPrice, signed } from "./shared";
import { hhmmToMinutes, isNum, num } from "./fmt";
import { riskBand } from "../../lib/riskMath";

/*
 * POST-ENTRY COCKPIT (2026-10-09 rework)
 *
 * An open position is judged against ITS OWN plan — the stop, the break-even
 * lock and the square-off the row carries (`exit_rules`, stored at entry by
 * the rules engine) — not against the dashboard's direction read or a
 * hard-coded clock. The old "IV crush" / "Risk elevated" / "Time exit zone"
 * chips nudged exactly the early exits the research found lose for a buyer
 * (puts are held to close with a 50% stop; never cut early).
 */

/* Manual rows are managed on /manual-trader, rule rows on /autotrader. */
export const managePathFor = (trade) => (trade?.strategy_id === "manual" ? "/manual-trader" : "/autotrader");

/* Loss budget left + open ₹ risk over BOTH books, and the open count with a
 * LIVE badge, so a live position never hides behind a paper-mode header. */
export function CockpitRiskBand({ status }) {
  if (!status) return null;
  const band = riskBand(status);
  const inr = (n) => `₹${fmtPrice(n)}`;
  return (
    <div data-testid="cockpit-risk-band" className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-800 px-4 py-2 text-[11px] font-semibold tabular-nums text-slate-400"
      title={`Loss budget left = max daily loss − today's losses (${status.is_paper === false ? "LIVE" : "PAPER"} mode, realised + open, the figure the risk engine checks). Open risk = Σ (entry − effective stop) × qty over every open row, both books.`}>
      <span><span className="uppercase tracking-wider text-slate-600">Loss budget left </span><b className={band.budgetLeft === 0 ? "text-rose-300" : "text-hi"}>{band.budgetLeft == null ? "—" : inr(band.budgetLeft)}</b></span>
      <span><span className="uppercase tracking-wider text-slate-600">Open risk </span><b className={band.openRisk > 0 ? "text-amber-300" : "text-hi"}>{inr(band.openRisk)}</b></span>
      <span><span className="uppercase tracking-wider text-slate-600">Open · both books </span><b className="text-hi">{band.open}</b>
        {band.live > 0 && <span className="ml-1 rounded bg-rose-600 px-1 py-px text-[10px] font-black uppercase text-white">{band.live} live</span>}
      </span>
    </div>
  );
}

/* The plan a row is managed by, in plain words (tested). */
export function positionPlan(trade, session, minuteOfDay) {
  const ex = trade?.exit_rules || null;
  const entry = Number(trade?.entry_price) || 0;
  const ltp = trade?.ltp != null ? Number(trade.ltp) : null;
  const stop = Math.max(Number(trade?.stop_loss) || 0, Number(trade?.trailing_sl) || 0) || null;
  const pct = ltp != null && entry ? ((ltp - entry) / entry) * 100 : null;
  const out = { stop, ltp, entry, pct, lines: [], stage: null, squareOff: null, minutesToSquareOff: null, nearStop: false };
  if (ex && isNum(ex.sl_pct)) {
    const be = isNum(ex.breakeven_after_pct) && Number(ex.breakeven_after_pct) > 0 ? Number(ex.breakeven_after_pct) : null;
    const lock = be != null ? Number(ex.lock_profit_pct || 0) : null;
    const tgt = Number(ex.target_pct || 0);
    out.lines.push(`Stop −${num(ex.sl_pct, 0)}% (₹${fmtPrice(entry * (1 - Number(ex.sl_pct) / 100), 1)})`);
    if (be != null) {
      const armed = pct != null && pct >= be;
      out.stage = armed ? "locked" : "open";
      out.lines.push(armed
        ? `Up ${num(pct, 0)}%: stop moved to ${lock > 0 ? `+${num(lock, 0)}%` : "break-even"} (₹${fmtPrice(entry * (1 + (lock || 0) / 100), 1)})`
        : `Once up ${num(be, 0)}% the stop moves to ${lock > 0 ? `+${num(lock, 0)}%` : "break-even"}${pct != null ? ` (now ${pct >= 0 ? "+" : ""}${num(pct, 0)}%)` : ""}`);
    }
    if (ex.trailing_enabled && isNum(ex.trail_pct)) out.lines.push(`Trail ${num(ex.trail_pct, 0)}% after +${num(ex.trail_activation_pct, 0)}%`);
    out.lines.push(tgt > 0 ? `Target +${num(tgt, 0)}% (₹${fmtPrice(entry * (1 + tgt / 100), 1)})` : "No fixed target — the profit is the tail");
    out.squareOff = ex.eod_squareoff || null;
  } else if (trade?.target != null) {
    out.lines.push(`Target ₹${fmtPrice(trade.target, 1)}`);
  }
  if (!out.squareOff) out.squareOff = session?.eod_squareoff || null;
  if (out.squareOff && minuteOfDay != null) {
    const m = hhmmToMinutes(out.squareOff);
    if (m != null) out.minutesToSquareOff = Math.max(0, m - minuteOfDay);
  }
  if (ltp != null && stop != null && ltp <= stop * 1.05) out.nearStop = true;
  return out;
}

const PostEntryCockpit = memo(function PostEntryCockpit({ trades, activeIndex, view, session, minuteOfDay }) {
  const active = (trades || []).filter((trade) => trade.underlying === activeIndex);
  if (!active.length) return <EmptyData label={`No open ${activeIndex === "BANKNIFTY" ? "BANK NIFTY" : "NIFTY"} option position · cockpit will activate after entry`} />;
  return (
    <div className="divide-y divide-slate-800">
      {active.slice(0, 2).map((trade) => {
        // /autotrade/status open_trades: net_unrealized_pnl is the P&L after
        // costs, ltp the current premium. Both are null when no live quote.
        const pnl = Number(trade.net_unrealized_pnl ?? 0);
        const plan = positionPlan(trade, session, minuteOfDay);
        const { ltp, stop } = plan;
        // Distances in premium, and in spot points via |delta| when the row
        // carries greeks: a ₹6 stop on a 0.5-delta option is a 12-point move.
        const greeks = trade.greeks || null;
        const absDelta = greeks && greeks.delta != null && !Number.isNaN(Number(greeks.delta)) ? Math.abs(Number(greeks.delta)) : null;
        const qty = Number(trade.quantity) || 0;
        const toStop = ltp != null && stop != null ? ltp - stop : null;
        const pctOf = (dist) => (dist == null || !ltp ? null : (dist / ltp) * 100);
        const ptsOf = (dist) => (dist == null || !absDelta ? null : dist / absDelta);
        const thetaRs = greeks?.theta_per_hour != null && qty ? Math.abs(Number(greeks.theta_per_hour)) * qty : null;
        return <div key={trade.id} className="p-4" data-testid="cockpit-position">
          <div className="flex items-start justify-between gap-3"><div><p className="flex flex-wrap items-center gap-1.5 text-xs font-black text-hi">{trade.strategy_name || trade.underlying}<span className={`rounded px-1 py-px text-[10px] font-black uppercase ${trade.is_paper === false || trade.is_paper === 0 ? "bg-rose-600 text-white" : "bg-cyan-500/10 text-cyan-300"}`}>{trade.is_paper === false || trade.is_paper === 0 ? "Live" : "Paper"}</span><Link to={managePathFor(trade)} className="text-[11px] font-bold text-cyan-400">Manage</Link></p><p className="mt-0.5 text-[11px] text-slate-500">Entry {trade.entry_time?.slice?.(11, 16) || "—"} · Spot {fmtPrice(trade.spot_at_entry)}{greeks?.strike != null ? ` · ${fmtPrice(greeks.strike)} ${greeks.option_type || ""}` : trade.selected_strike != null ? ` · ${fmtPrice(trade.selected_strike)}` : ""}{qty ? ` · qty ${fmtPrice(qty)}` : ""}</p></div><div className="text-right"><p className={`text-lg font-black ${pnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{pnl >= 0 ? "+" : ""}₹{fmtPrice(pnl)}</p><p className="text-[11px] text-slate-500">{signed(trade.pnl_pct)}</p></div></div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[["Entry", trade.entry_price], ["Now", ltp], ["Stop", stop]].map(([label, value]) => <div key={label} className="rounded-lg bg-slate-950/60 p-2"><p className="text-[11px] uppercase text-slate-600">{label}</p><p className="text-xs font-black text-hi">₹{fmtPrice(value, 1)}</p></div>)}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-center sm:grid-cols-3">
            <div className={`rounded-lg border p-2 ${plan.nearStop ? "border-rose-500/40 bg-rose-500/[.10]" : "border-rose-500/20 bg-rose-500/[.05]"}`}>
              <p className="text-[11px] uppercase text-slate-600">Room to stop</p>
              <p className="text-xs font-black tabular-nums text-rose-300">{toStop == null ? "—" : `${fmtPrice(pctOf(toStop), 1)}%`}</p>
              <p className="text-[11px] tabular-nums text-slate-500">{ptsOf(toStop) == null ? "— pts" : `${fmtPrice(ptsOf(toStop))} spot pts`}</p>
            </div>
            <div className="rounded-lg bg-slate-950/60 p-2">
              <p className="text-[11px] uppercase text-slate-600">Square-off</p>
              <p className="text-xs font-black tabular-nums text-hi">{plan.squareOff || "—"}</p>
              <p className="text-[11px] tabular-nums text-slate-500">{plan.minutesToSquareOff == null ? "" : plan.minutesToSquareOff > 0 ? `in ${plan.minutesToSquareOff} min` : "due now"}</p>
            </div>
            <div className="rounded-lg bg-slate-950/60 p-2">
              <p className="text-[11px] uppercase text-slate-600">Holding cost</p>
              <p className="text-xs font-black tabular-nums text-hi">{thetaRs == null ? "—" : `₹${fmtPrice(thetaRs)}/h`}</p>
              <p className="text-[11px] tabular-nums text-slate-500">{greeks?.iv == null ? "IV —" : `IV ${fmtPrice(greeks.iv, 1)}%`}</p>
            </div>
          </div>
          {plan.lines.length > 0 && (
            <ul className="mt-3 space-y-0.5 text-[12px] text-slate-300" data-testid="cockpit-plan">
              {plan.lines.map((line) => <li key={line} className="flex items-start gap-1.5"><span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${plan.stage === "locked" && line.startsWith("Up") ? "bg-emerald-400" : "bg-slate-500"}`} />{line}</li>)}
            </ul>
          )}
          {plan.nearStop && <p className="mt-2 inline-block rounded bg-rose-500/10 px-2 py-1 text-[11px] font-bold text-rose-300">Within 5% of the stop — let the stop do its job</p>}
        </div>;
      })}
    </div>
  );
});

export default PostEntryCockpit;
